import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, Text, Button, makeStyles, tokens } from '@fluentui/react-components';
import { HomeRegular } from '@fluentui/react-icons';
import DrillDownView from '../DrillDownView';
import Logo from '../Logo';
import { fetchIssues } from '../../services/api';
import { Issue, DrillDownItem } from '../../types';

interface HomeViewProps {
  apiUrl?: string;
  onNavigate?: (path: string) => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    height: 'calc(100vh - 2rem)',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL,
  },
  leftColumn: {
    width: '68%',
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingRight: tokens.spacingVerticalL,
    overflowY: 'auto',
  },
  rightColumn: {
    width: '32%',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalL,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: tokens.spacingVerticalM,
  },
  title: {
    marginTop: tokens.spacingVerticalL,
    marginBottom: tokens.spacingVerticalS,
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80vh',
    gap: tokens.spacingVerticalM,
  },
  placeholderContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
  placeholderText: {
    color: tokens.colorNeutralForeground3,
    maxWidth: '20rem',
    textAlign: 'center',
    display: 'block',
  },
});

const HomeView: React.FC<HomeViewProps> = ({ apiUrl, onNavigate }) => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const styles = useStyles();

  useEffect(() => {
    const loadIssues = async (): Promise<void> => {
      try {
        const data = await fetchIssues();
        setIssues(data);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load issues:', error);
        setLoading(false);
      }
    };
    loadIssues();
  }, []);

  // Get unique domains
  const domains = useMemo<DrillDownItem[]>(() => {
    const domainMap = new Map<string, DrillDownItem>();
    issues.forEach((issue) => {
      if (!domainMap.has(issue.Taxonomy.Domain)) {
        // Extract the first number from the Path (e.g., "1.1.1.1.1" -> 1)
        const pathNumber = parseInt(issue.Path.split('.')[0], 10);
        domainMap.set(issue.Taxonomy.Domain, {
          label: issue.Taxonomy.Domain,
          value: issue.Taxonomy.Domain,
          pathNumber: pathNumber,
          count: 0,
        });
      }
      // Increment count
      const domain = domainMap.get(issue.Taxonomy.Domain);
      if (domain) {
        domain.count = (domain.count || 0) + 1;
      }
    });
    return Array.from(domainMap.values())
      .sort((a, b) => (a.pathNumber || 0) - (b.pathNumber || 0));
  }, [issues]);

  const handleDomainClick = (domain: DrillDownItem): void => {
    const path = `/${encodeURIComponent(domain.value)}`;
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const handleReset = (): void => {
    if (onNavigate) {
      onNavigate('/');
    } else {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" />
        <Text>Loading issues...</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* LEFT COLUMN - Navigation */}
      <div className={styles.leftColumn}>
        <div className={styles.header}>
          <Logo />
          <Text as="h1" size={800} weight="bold" className={styles.title}>
            TRACE
          </Text>
          <Text size={500} className={styles.subtitle}>
            Taxonomy for Resolution, Accuracy, Communication, and Efficiency
          </Text>
        </div>
        <div className={styles.headerRow}>
          <Text size={500} weight="semibold">Domains</Text>
          <Button
            icon={<HomeRegular />}
            appearance="subtle"
            onClick={handleReset}
            size="small"
          >
            Reset
          </Button>
        </div>
        <DrillDownView
          items={domains}
          onItemClick={handleDomainClick}
        />
      </div>

      {/* RIGHT COLUMN - Issue List or Placeholder */}
      <div className={styles.rightColumn}>
        <div className={styles.placeholderContainer}>
          <Text size={500} weight="semibold" style={{ marginBottom: tokens.spacingVerticalM }}>
            Start Exploring
          </Text>
          <Text size={400} className={styles.placeholderText}>
            Select a domain from the left to begin browsing issues and their resolution procedures.
          </Text>
        </div>
      </div>
    </div>
  );
};

export default HomeView;

